/* import __COLOCATED_TEMPLATE__ from './phone-handover-form.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Args {
  ruleset: $TSFixMe;
  numberConfiguration: FinVoiceNumberConfiguration;
  updateHandover: (handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration) => void;
  closeSection: () => void;
}

type HandoverType = 'handover-to-voicemail' | 'handover-to-human';

const DEFAULT_DEFLECTION_TIMER = 300; // 5 minutes

export default class PhoneHandoverForm extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  @tracked configBeingEdited: 'default' | 'in' | 'nin' = 'default';
  @tracked clonedHandoverConfig: FinVoiceNumberConfigurationHandoverConfiguration = {};

  get savingDisabled() {
    return (
      !!this.handoverConfigError ||
      this.args.ruleset.isSaving ||
      this.args.numberConfiguration?.isSaving
    );
  }

  get ruleset() {
    return this.args.ruleset;
  }

  get isDisabled() {
    return !this.numberConfiguration;
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.setInitialHandoverConfig();
  }

  get isAnytimeConfig() {
    return !this.numberConfiguration?.predicates?.officeHoursComparison;
  }

  get handoverOptions() {
    return [
      {
        icon: 'phone-missed',
        value: 'handover-to-voicemail',
        text: this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-voicemail',
        ),
      },
      {
        icon: 'phone-hold',
        value: 'handover-to-human',
        text: this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-human',
        ),
      },
    ];
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get relevantHandoverConfig() {
    let officeHoursComparison = this.configBeingEdited;

    if (officeHoursComparison) {
      let handoverConfig = this.clonedHandoverConfig[officeHoursComparison];

      if (handoverConfig) {
        return handoverConfig;
      }
    }

    return this.clonedHandoverConfig['default'];
  }

  get handoverType() {
    return this.relevantHandoverConfig?.type;
  }

  get assignableAdmins() {
    return this.appService.app.assignableAdmins;
  }

  get assigneeId() {
    return this.relevantHandoverConfig?.config?.assigneeId;
  }

  get deflectionTimer() {
    return this.relevantHandoverConfig.type === 'handover-to-human'
      ? (this.relevantHandoverConfig?.config?.deflectionTimer || DEFAULT_DEFLECTION_TIMER) / 60
      : undefined;
  }

  set deflectionTimer(value: number | undefined) {
    if (value === undefined) {
      return;
    }

    if (!this.relevantHandoverConfig) {
      return;
    }

    if (this.relevantHandoverConfig.type !== 'handover-to-human') {
      return;
    }

    if (!this.relevantHandoverConfig.config) {
      this.relevantHandoverConfig.config = {};
    }

    this.relevantHandoverConfig.config.deflectionTimer = value * 60;
  }

  get handoverConfigError() {
    if (!this.clonedHandoverConfig || !Object.keys(this.clonedHandoverConfig).length) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.error.no-configuration',
      );
    }

    for (let [key, config] of Object.entries(this.clonedHandoverConfig)) {
      let label =
        key === 'in'
          ? this.intl.t(
              'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.in-label',
            )
          : this.intl.t(
              'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.nin-label',
            );

      if (!config.type) {
        return this.intl.t(
          `operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.error.no-type${this.isAnytimeConfig ? '-with-key' : ''}`,
          { label },
        );
      }

      if (config.type === 'handover-to-voicemail' || config.type === 'handover-to-human') {
        if (!config.config?.assigneeId) {
          return this.intl.t(
            `operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.error.no-assignee${this.isAnytimeConfig ? '-with-key' : ''}`,
            { label },
          );
        }
      }
    }

    return undefined;
  }

  getConfigForComparison(comparison: 'in' | 'nin' | 'default') {
    return {
      ...(this.numberConfiguration.handoverConfiguration[comparison] ||
        this.numberConfiguration.handoverConfiguration['default']),
    };
  }

  @action
  setInitialHandoverConfig() {
    this.configBeingEdited = this.isAnytimeConfig
      ? 'in'
      : this.numberConfiguration?.predicates?.officeHoursComparison || 'default';

    let handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration = {};

    if (this.isAnytimeConfig) {
      handoverConfig['in'] = this.getConfigForComparison('in');
      handoverConfig['nin'] = this.getConfigForComparison('nin');
    } else {
      handoverConfig[this.configBeingEdited] = this.getConfigForComparison(this.configBeingEdited);
    }

    this.clonedHandoverConfig = handoverConfig;
  }

  @action
  changeConfigBeingEdited(config: 'in' | 'nin') {
    this.configBeingEdited = config;
  }

  @action
  setHandoverType(type: HandoverType) {
    if (this.relevantHandoverConfig) {
      this.relevantHandoverConfig.type = type;
      this.relevantHandoverConfig.config = {};
    } else {
      this.numberConfiguration.handoverConfiguration[this.configBeingEdited] = {
        type,
        config: {
          assigneeId: undefined,
        },
      };
    }

    this.clonedHandoverConfig = { ...this.clonedHandoverConfig };
  }

  @action
  async updateHandover() {
    if (this.handoverConfigError) {
      return;
    }

    this.args.updateHandover(this.clonedHandoverConfig);
  }

  @action
  setAssigneeId(assigneeId: string) {
    if (this.relevantHandoverConfig) {
      if (!this.relevantHandoverConfig.config) {
        this.relevantHandoverConfig.config = {};
      }

      this.relevantHandoverConfig.config.assigneeId = assigneeId;

      this.clonedHandoverConfig = { ...this.clonedHandoverConfig };
    }
  }

  @action
  setDeflectionTimer(value: number) {
    if (!this.relevantHandoverConfig) {
      return;
    }

    if (this.relevantHandoverConfig.type !== 'handover-to-human') {
      return;
    }

    if (!this.relevantHandoverConfig.config) {
      this.relevantHandoverConfig.config = {};
    }

    this.relevantHandoverConfig.config.deflectionTimer = value * 60;
    this.clonedHandoverConfig = { ...this.clonedHandoverConfig };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::PhoneHandover::PhoneHandoverForm': typeof PhoneHandoverForm;
  }
}
